/**
 * 🔴 AI-INTERP N3 (@UC0162) — TỪ VỰNG DANH TỪ ĐẾM NGƯỜI: MỘT nguồn cho mọi lưới đọc "số + người" / "mỗi + người".
 *
 * ## Vấn đề (đo bằng chạy hàm thuần, 23/10)
 * Năm tập chép tay lệch nhau — bộ đổi tiền (`money-parser`), lưới ô tiền S19 (`money-fallback`), lưới mở đợt thu (`collection-open`),
 * lưới danh sách thành viên (`member-list`), bộ viết lại số chữ (`vi-number-words`). Tập của bộ đổi tiền chỉ có "người"/"thành viên" CÓ DẤU ⇒
 * *"chi 1tr2 nguoi an trua"* · *"chi 1tr2 bạn"* · *"chi 1tr2 bé"* đọc thẳng 1.200.000 (đuôi ghép hay số người? — phải hỏi như "1tr2 người").
 *
 * ## Luật
 * Mỗi lưới LẤY tập người từ đây, rồi thêm/bớt phần RIÊNG của nó ngay ở file của lưới, có tên và lý do (đơn vị kỳ "tháng", viết tắt "hs",
 * "con" cố ý vắng…). Không lưới nào tự chép lại tập người. Mỗi mục mang HAI dạng — có dấu và không dấu; test khoá `plain === normalizeViText(vi)`.
 * File này KHÔNG import gì: `vi-number-words` (bị `vi-normalizer` import) cũng dùng nó — không được tạo vòng import.
 */
export interface CountNoun {
  /** Viết đúng chính tả, chữ thường, NFC; hai tiếng cách một khoảng trắng. */
  readonly vi: string;
  /** Dạng gõ không dấu (`normalizeViText(vi)`). */
  readonly plain: string;
  /**
   * Dạng không dấu trùng một chữ THƯỜNG GẶP cạnh số tiền ("cho" ≡ chỗ · "ve" ≡ về · "ngay" ≡ ngày · "lan" ≡ Lan) ⇒ chỉ dạng CÓ DẤU được tính.
   * Gõ không dấu thì không phân biệt được ⇒ không coi là danh từ (chiều an toàn của bộ đọc tiền: một đề cập số không giải thích được ⇒ hỏi).
   */
  readonly accentedOnly?: true;
}

const noun = (vi: string, plain: string): CountNoun => Object.freeze({ vi, plain });
const accentedOnly = (vi: string, plain: string): CountNoun => Object.freeze({ vi, plain, accentedOnly: true as const });

/**
 * Danh từ người/suất mà ĐỨNG SAU MỘT SỐ thì là số ĐẦU NGƯỜI — không có nghĩa thường gặp nào khác ở vị trí đó ("2 bạn", "3 bé", "30 suất").
 * Dùng được cho cả lưới "số + danh từ ⇒ số lượng, không phải tiền" lẫn lưới "mỗi + người".
 */
export const HEAD_COUNT_NOUNS: readonly CountNoun[] = Object.freeze([
  noun("người", "nguoi"),
  noun("bạn", "ban"),
  noun("bé", "be"),
  noun("cháu", "chau"),
  noun("đứa", "dua"),
  noun("suất", "suat"),
  noun("thành viên", "thanh vien"),
  noun("học sinh", "hoc sinh"),
  noun("học viên", "hoc vien"),
  noun("sinh viên", "sinh vien"),
  noun("phụ huynh", "phu huynh"),
  noun("gia đình", "gia dinh"),
]);

/**
 * Danh từ người mà sau một SỐ TIỀN còn nghĩa KHÁC rất thường gặp: "em"/"con" (đại từ — *"chuyển 1tr2 em nhé"*), "hộ" (giới từ — *"nộp 200k hộ
 * Lan"*), "nhà" (*"tiền nhà"*). Lưới "mỗi <người>" (sau "mỗi" thì chỉ có nghĩa người) dùng được; lưới "số + danh từ ⇒ không phải tiền" KHÔNG dùng
 * (đọc "em" thành số người là biến câu rõ thành câu hỏi lại oan).
 */
export const PRONOUN_LIKE_PERSON_NOUNS: readonly CountNoun[] = Object.freeze([
  noun("em", "em"),
  noun("con", "con"),
  noun("hộ", "ho"),
  noun("nhà", "nha"),
]);

/**
 * Các dạng chữ (có dấu, và — khi `plain` — không dấu) của một tập, bỏ trùng, dài trước (cụm hai tiếng thắng một tiếng đầu của nó). Mục `accentedOnly`
 * KHÔNG bao giờ góp dạng không dấu (vá review C2: cờ từng bị bỏ qua — "co" = có · "ba" = ba/bà sẽ lọt vào regex của caller).
 */
export function countNounForms(nouns: readonly CountNoun[], opts: { readonly plain: boolean }): readonly string[] {
  const forms = new Set<string>();
  for (const n of nouns) {
    forms.add(n.vi);
    if (opts.plain && n.accentedOnly !== true) forms.add(n.plain);
  }
  return [...forms].sort((a, b) => b.length - a.length);
}

/** Nguồn regex (không neo, không cờ) khớp MỘT dạng của tập; khoảng trắng giữa hai tiếng được gõ dính hoặc thừa (`\s*`). */
export function countNounAlternation(forms: readonly string[]): string {
  return [...forms]
    .sort((a, b) => b.length - a.length)
    .map((f) => f.split(" ").join("\\s*"))
    .join("|");
}

/**
 * 🔴 AI-INTERP N1 (@UC0209) — LOẠI TỪ / ĐƠN VỊ ĐẾM đứng NGAY SAU một số trần nhỏ ⇒ số đó là SỐ LƯỢNG ("3 ly cafe" · "2 thùng nước" · "bé 5 tuổi").
 * Tập ĐÓNG — đây là danh sách CÁI ĐƯỢC THA: số trần nhỏ không được giải thích (kỳ · ngày · năm · giờ · số lượng · nhãn) là đề cập CHƯA RÕ VAI, và
 * câu còn một đề cập như thế bên cạnh một số tiền thì bộ đọc HỎI. Thiếu một từ ở đây ⇒ hỏi thừa (an toàn); thừa một từ ⇒ có thể bỏ sót một
 * số tiền ("300 cho sân") ⇒ ĐỪNG thêm chữ nào cũng là hư từ/động từ hay đi sau số tiền. CỐ Ý VẮNG: "lít"/"xị"/"cành" (tiếng lóng tiền: "3 lít" =
 * 300k) — đơn vị đo khác ở `DECIMAL_MEASURE_AFTER_RE` của bộ đọc tiền chỉ áp cho số thập phân.
 */
export const CLASSIFIER_NOUNS: readonly CountNoun[] = Object.freeze([
  // vá review MED-1: vật đếm hay gặp ở nhóm phụ huynh / đội
  noun("ổ", "o"), noun("nải", "nai"), noun("bản", "ban"),
  noun("cái", "cai"), accentedOnly("con", "con"), noun("chiếc", "chiec"), accentedOnly("quả", "qua"), noun("trái", "trai"), noun("ly", "ly"),
  accentedOnly("cốc", "coc"), noun("chai", "chai"), noun("lon", "lon"), noun("thùng", "thung"), noun("hộp", "hop"), noun("gói", "goi"),
  noun("bao", "bao"), accentedOnly("túi", "tui"), accentedOnly("vé", "ve"), noun("món", "mon"), noun("phần", "phan"), noun("đôi", "doi"),
  noun("cặp", "cap"), noun("bộ", "bo"), noun("cuốn", "cuon"), noun("quyển", "quyen"), accentedOnly("tờ", "to"), noun("két", "ket"),
  noun("bình", "binh"), noun("cây", "cay"), noun("que", "que"), noun("hũ", "hu"), noun("lọ", "lo"), noun("bó", "bo"), noun("bịch", "bich"),
  noun("kg", "kg"), noun("ký", "ky"), noun("kí", "ki"), noun("cân", "can"), noun("lạng", "lang"), noun("mét", "met"), noun("tấn", "tan"),
  noun("km", "km"), noun("giờ", "gio"), noun("phút", "phut"), noun("tiếng", "tieng"), noun("buổi", "buoi"), accentedOnly("ngày", "ngay"),
  noun("đêm", "dem"), noun("tuần", "tuan"), noun("tháng", "thang"), accentedOnly("năm", "nam"), accentedOnly("lần", "lan"), noun("lượt", "luot"),
  noun("đợt", "dot"), accentedOnly("chuyến", "chuyen"), noun("xe", "xe"), accentedOnly("chỗ", "cho"), noun("ghế", "ghe"), noun("bàn", "ban"),
  noun("phòng", "phong"), noun("sân", "san"), noun("trận", "tran"), accentedOnly("ván", "van"), noun("hiệp", "hiep"), accentedOnly("ca", "ca"),
  noun("tuổi", "tuoi"), accentedOnly("tô", "to"), noun("đĩa", "dia"), noun("chén", "chen"), noun("bát", "bat"), noun("mâm", "mam"),
  accentedOnly("nồi", "noi"), noun("bữa", "bua"), noun("tiết", "tiet"), noun("môn", "mon"), noun("khoá", "khoa"), noun("khóa", "khoa"),
  noun("lớp", "lop"), noun("nhóm", "nhom"), noun("đội", "doi"), noun("tổ", "to"), noun("câu", "cau"), noun("bài", "bai"), noun("trang", "trang"),
  noun("tập", "tap"), accentedOnly("ảnh", "anh"), accentedOnly("lá", "la"), noun("bức", "buc"), noun("tấm", "tam"), noun("đơn", "don"),
  noun("kiện", "kien"), noun("tầng", "tang"), noun("căn", "can"), noun("lô", "lo"), noun("set", "set"), noun("combo", "combo"),
  noun("slot", "slot"), noun("kỳ", "ky"), noun("kì", "ki"), noun("quý", "quy"), noun("quí", "qui"),
]);

/**
 * 🔴 AI-INTERP N1 vá review MED-1 (@UC0209) — NGƯỜI theo vai xưng hô ("2 cô" · "3 thầy" · "2 bác") + viết tắt ("12 ng") — sau một số trần nhỏ là SỐ NGƯỜI.
 * Dạng không dấu trùng chữ thường ("co" = có · "di" = đi · "bac" = bạc · "ba" = ba/bà) ⇒ chỉ dạng có dấu. "anh"/"chị" cố ý VẮNG ("300 anh trả" — đại từ).
 */
export const KINSHIP_PERSON_NOUNS: readonly CountNoun[] = Object.freeze([
  accentedOnly("cô", "co"), noun("thầy", "thay"), accentedOnly("bác", "bac"), accentedOnly("chú", "chu"), noun("ông", "ong"), accentedOnly("bà", "ba"),
  accentedOnly("dì", "di"), accentedOnly("cậu", "cau"), noun("mợ", "mo"), accentedOnly("cụ", "cu"), noun("ng", "ng"), noun("tv", "tv"), noun("hs", "hs"),
  noun("ph", "ph"),
]);

/**
 * 🔴 AI-INTERP N1 (@UC0209) — danh từ NHÃN đứng NGAY TRƯỚC một số trần nhỏ ⇒ số đó là tên/số thứ tự ("lớp 5" · "phòng 301" · "con thứ 2" · "ngày 5").
 * Cùng hướng với `CLASSIFIER_NOUNS`: tập ĐÓNG cái được tha. Nhãn ĐỨNG SAU "tiền/phí/giá" thì là MỤC ĐÍCH, không phải nhãn ("tiền sân 300" ⇒ chưa rõ) —
 * luật đó ở bộ đọc tiền. CỐ Ý VẮNG: "hàng" (tiền hàng) · dạng không dấu "thu" (= thu tiền), "lan"/"nam" (tên), "quy" (= quỹ), "ma" (= mà), "day" (= đấy).
 */
export const LABEL_NOUNS_BEFORE_NUMBER: readonly CountNoun[] = Object.freeze([
  noun("lớp", "lop"), noun("tổ", "to"), noun("đội", "doi"), noun("nhóm", "nhom"), noun("phòng", "phong"), noun("số", "so"), accentedOnly("thứ", "thu"),
  noun("đợt", "dot"), noun("kỳ", "ky"), noun("kì", "ki"), noun("khối", "khoi"), noun("khoá", "khoa"), noun("khóa", "khoa"), noun("tầng", "tang"),
  noun("nhà", "nha"), noun("sân", "san"), noun("bàn", "ban"), noun("câu", "cau"), noun("bài", "bai"), noun("mục", "muc"), noun("trang", "trang"),
  accentedOnly("lần", "lan"), noun("buổi", "buoi"), noun("ngày", "ngay"), noun("mùng", "mung"), noun("hạn", "han"), noun("tuần", "tuan"),
  accentedOnly("quý", "quy"), accentedOnly("quí", "qui"), accentedOnly("năm", "nam"), noun("ca", "ca"), noun("tiết", "tiet"), noun("chương", "chuong"),
  noun("tập", "tap"), noun("phần", "phan"), accentedOnly("mã", "ma"), noun("đơn", "don"), noun("căn", "can"), noun("lô", "lo"), noun("hẻm", "hem"),
  noun("ngõ", "ngo"), noun("ngách", "ngach"), noun("kiệt", "kiet"), noun("quận", "quan"), noun("phường", "phuong"), noun("xóm", "xom"),
  noun("khu", "khu"), accentedOnly("dãy", "day"), noun("ghế", "ghe"), noun("xe", "xe"), noun("lúc", "luc"), noun("top", "top"), noun("học kỳ", "hoc ky"),
  // vá review MED-1: nhãn THỜI GIAN ("hôm 20" · "sáng 25" · "chủ nhật 26") — "toi" không dấu = tôi ⇒ chỉ có dấu
  noun("hôm", "hom"), noun("sáng", "sang"), noun("chiều", "chieu"), accentedOnly("tối", "toi"), noun("trưa", "trua"), noun("đêm", "dem"),
  noun("chủ nhật", "chu nhat"),
]);
